
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, ContactIcon, FileClock, Calculator } from "lucide-react";
import { LayoutItem } from "../utils";
import { ResizableSwappableCard } from "@/components/admin/dashboard/ResizableSwappableCard";
import { useEffect, useState } from "react";
import { currencyConverter } from "@/services/currencyConverter";
import { useEnhancedTranslation } from "@/hooks/useEnhancedTranslation";

interface StatCardProps {
  id: string;
  size: 'small' | 'medium' | 'large' | 'xlarge';
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
  handlesVisible: boolean;
  onRemove?: (id: string) => void;
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
}

export const StatCard = ({
  id,
  size,
  title,
  value,
  subtitle,
  icon,
  handlesVisible,
  onRemove,
  onResize
}: StatCardProps) => (
  <ResizableSwappableCard 
    id={id}
    size={size}
    className="h-full" 
    showHandle={handlesVisible}
    showResizeControl={handlesVisible}
    onRemove={handlesVisible ? onRemove : undefined}
    onResize={onResize ? (newSize) => onResize(id, newSize) : undefined}
  >
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium">{title}</CardTitle>
      {icon}
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
      <p className="text-xs text-muted-foreground">
        {subtitle}
      </p>
    </CardContent>
  </ResizableSwappableCard>
);

export const TotalContractsCard = ({ 
  item, 
  userStats, 
  handlesVisible, 
  onRemove, 
  onResize 
}: { 
  item: LayoutItem; 
  userStats: any; 
  handlesVisible: boolean;
  onRemove?: (id: string) => void;
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
}) => {
  const { t } = useEnhancedTranslation();
  
  return (
    <StatCard
      id={item.id}
      size={item.size}
      title={t('dashboard.widgets.totalContracts', 'Total Contracts')}
      value={userStats.totalContracts}
      subtitle={t('dashboard.widgets.acrossCategories', 'Across all categories')}
      icon={<FileText className="h-4 w-4 text-muted-foreground" />}
      handlesVisible={handlesVisible}
      onRemove={onRemove}
      onResize={onResize}
    />
  );
};

export const ActiveContractsCard = ({ 
  item, 
  userStats, 
  handlesVisible, 
  onRemove, 
  onResize 
}: { 
  item: LayoutItem; 
  userStats: any; 
  handlesVisible: boolean;
  onRemove?: (id: string) => void;
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
}) => {
  const { t } = useEnhancedTranslation();
  
  return (
    <StatCard
      id={item.id}
      size={item.size}
      title={t('dashboard.widgets.activeContracts', 'Active Contracts')}
      value={userStats.activeContracts}
      subtitle={t('dashboard.widgets.currentlyActive', 'Currently active')}
      icon={<ContactIcon className="h-4 w-4 text-muted-foreground" />}
      handlesVisible={handlesVisible}
      onRemove={onRemove}
      onResize={onResize}
    />
  );
};

export const ExpiringContractsCard = ({ 
  item, 
  userStats, 
  handlesVisible, 
  onRemove, 
  onResize 
}: { 
  item: LayoutItem; 
  userStats: any; 
  handlesVisible: boolean;
  onRemove?: (id: string) => void;
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
}) => {
  const { t } = useEnhancedTranslation();
  
  return (
    <StatCard
      id={item.id}
      size={item.size}
      title={t('dashboard.widgets.expiringSoon', 'Expiring Soon')}
      value={userStats.expiringContracts}
      subtitle={t('dashboard.widgets.nextDays', 'In the next 30 days')}
      icon={<FileClock className="h-4 w-4 text-muted-foreground" />}
      handlesVisible={handlesVisible}
      onRemove={onRemove}
      onResize={onResize}
    />
  );
};

export const TotalValueCard = ({ 
  item, 
  userStats, 
  defaultCurrency,
  handlesVisible, 
  onRemove, 
  onResize 
}: { 
  item: LayoutItem; 
  userStats: any;
  defaultCurrency: string | null;
  handlesVisible: boolean;
  onRemove?: (id: string) => void;
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
}) => {
  const { t } = useEnhancedTranslation();
  const [convertedTotal, setConvertedTotal] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  useEffect(() => {
    const calculateTotal = async () => {
      if (!userStats || !userStats.valuesByCurrency || !userStats.defaultCurrencyId) {
        setConvertedTotal(userStats?.totalValue || 0);
        return;
      }
      
      setIsLoading(true);
      let total = 0;
      
      try {
        // Convert each currency amount to the default currency
        for (const currencyValue of userStats.valuesByCurrency) {
          const { currencyId, total: amount, symbol, code } = currencyValue;
          
          if (currencyId === userStats.defaultCurrencyId) {
            // No conversion needed
            total += amount;
          } else {
            // Convert to default currency
            const { convertedAmount, rate } = await currencyConverter.convert(
              amount,
              currencyId,
              userStats.defaultCurrencyId
            );
            
            total += convertedAmount;
          }
        }
        
        setConvertedTotal(total);
      } catch (error) {
        console.error('Error converting currencies:', error);
        // Fallback to basic sum without conversion
        setConvertedTotal(userStats.totalValue || 0);
      } finally {
        setIsLoading(false);
      }
    };
    
    calculateTotal();
  }, [userStats, defaultCurrency]);
  
  const displayValue = isLoading
    ? t('dashboard.widgets.calculating', 'Calculating...')
    : `${defaultCurrency || ''}${(convertedTotal || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  
  return (
    <StatCard
      id={item.id}
      size={item.size}
      title={t('dashboard.widgets.totalValue', 'Total Value')}
      value={displayValue}
      subtitle={t('dashboard.widgets.inUserCurrency', 'In user\'s currency')}
      icon={<Calculator className="h-4 w-4 text-muted-foreground" />}
      handlesVisible={handlesVisible}
      onRemove={onRemove}
      onResize={onResize}
    />
  );
};
